import React from 'react';
import { Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { getTagForChangeTypes, getTagForStates } from '../../Common/Tags';
import { changeTypeColor, DEFAULT_CHANGE_TYPE_COLOR } from '../../ChangeInstances/List/constants';
import '../../ChangeInstances/List/ChangeInstancesList.css';

/**
 * One field of a proposed change. The label is rendered at every width — the
 * wide layout hides it behind the column header above it, the narrow layout
 * shows it, because there is no header to stand under once the row stacks.
 */
const Field = ({ label, value }: { label: string; value?: string }) => (
    <div className="pci-field">
        <span className="pci-field__label">{label}</span>
        <span className="pci-field__value" title={value || undefined}>{value || '—'}</span>
    </div>
);

/**
 * What submitting the declaration would produce, in the change instances
 * list's visual language — the same palette, the same tags, the same accent
 * per change type.
 *
 * Rows only. There is nothing behind them to open: a proposed change has no
 * id, no history and no detail page, and the declaration it would write is
 * the one on screen in the editor above.
 */
const ChangeInstancesValidators = ({ change_instances }: { change_instances: Record<string, any>[] }) => {
    const changes = change_instances ?? [];

    if (changes.length === 0) return null;

    return (
        <section className="netorca-ci netorca-sb-proposed" aria-label="Proposed change instances">
            <header className="pci-head">
                <h3 className="pci-head__title">Proposed change instances</h3>
                <Tooltip title="These change instances will be generated if you submit the declaration.">
                    <QuestionCircleOutlined aria-hidden="true" />
                </Tooltip>
                <span className="pci-head__count">{changes.length}</span>
            </header>

            {/* Column headers belong to the wide layout only; once a row stacks,
                each field carries its own label instead. */}
            <div className="pci-columns" aria-hidden="true">
                <span>ID</span>
                <span>Service Item</span>
                <span>Service Owner</span>
                <span>Application</span>
                <span>Service</span>
                <span>Change</span>
            </div>

            <ul className="pci-list">
                {changes.map((change, index) => {
                    const accent = changeTypeColor[change.change_type] || DEFAULT_CHANGE_TYPE_COLOR;
                    const isNew = change.id == null;

                    return (
                        <li
                            key={change.service_item?.id ?? `${change.service_item?.name}-${index}`}
                            className="pci-row"
                            style={{ ['--ci-accent' as any]: accent }}
                        >
                            {/* a change with no id does not exist yet — saying
                                "new" is truer than an empty cell */}
                            <span className="pci-id">
                                {isNew ? <em className="pci-id__new">new</em> : `#${change.id}`}
                            </span>

                            <span className="pci-name" title={change.service_item?.name}>
                                {change.service_item?.name || '—'}
                            </span>

                            <Field label="Service Owner" value={change.service_owner_team?.name} />
                            <Field label="Application" value={change.application?.name} />
                            <Field label="Service" value={change.service?.name} />

                            <span className="pci-tags">
                                {getTagForChangeTypes(change.change_type)}
                                {change.state && getTagForStates(change.state)}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
};

export default ChangeInstancesValidators;
